import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Spin, Input } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import RouteCard from "./RouteCard";

// Task 2187 (F5) -- Supplier's "Returns Inbound" screen (plan §14.2: GET /api/DiamondShipment/
// Inbound). Not in the original prototype file by that exact name, but the capability is: open
// returns first, one action -- Mark received (relist). Built as a simple card list, same pattern
// as SupplierRequests.js, since no literal prototype markup exists to port for this one screen.

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

const toRouteAddress = (snap) =>
  snap && { Label: snap.label, ContactName: snap.contact, Address: snap.line1, City: snap.city, State: snap.state, Zip: snap.zip, Country: snap.country, Phone: snap.phone };

export const ReturnsInbound = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [notes, setNotes] = useState({});

  const load = () => {
    if (!dealerId) return;
    setLoading(true);
    PurchaseOrderService.GetInboundReturns({ DealerID: String(dealerId) })
      .then((response) => {
        setLoading(false);
        setShipments(response.data.status ? response.data.responseData : []);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  const handleReceive = (shipmentId) => {
    setActing(true);
    PurchaseOrderService.ReceiveDiamondShipment(shipmentId, { DealerID: String(dealerId), ConditionNote: notes[shipmentId] || "" })
      .then((response) => {
        setActing(false);
        if (response.data.status === true) {
          NotificationManager.success(t("Received — relisted"));
          load();
        } else {
          NotificationManager.error(t(response.data.message || "Could not mark this received"));
        }
      })
      .catch(() => setActing(false));
  };

  return (
    <div style={{ padding: 20 }}>
      <Spin spinning={loading || acting}>
        <h2 style={{ margin: 0 }}>{t("Returns Inbound")}</h2>
        <p style={{ color: "#888", margin: 0, marginBottom: 16 }}>{t("Open returns waiting to be received")}</p>

        {shipments.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>✓</div>
            {t("Nothing waiting.")}
          </div>
        ) : (
          shipments.map((s) => (
            <div key={s.ShipmentID} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, display: "inline" }}>{s.DocNumber}</h3>{" "}
                  <span style={{ color: "#888", fontSize: 13 }}>
                    {s.RetailerName} · {t("{{n}} stone(s)", { n: s.LineCount })} · {t("shipped")} {fmtDate(s.ShippedUtc)}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Button size="small" onClick={() => navigate(`/document/${s.DiamondDocID}`)}>{t("Open")}</Button>
                  <Input
                    size="small"
                    style={{ width: 180 }}
                    placeholder={t("Condition on receipt (optional)")}
                    value={notes[s.ShipmentID] || ""}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [s.ShipmentID]: e.target.value }))}
                  />
                  <Button size="small" type="primary" onClick={() => handleReceive(s.ShipmentID)}>
                    {t("Mark received")}
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <RouteCard
                  fromAddress={toRouteAddress(typeof s.FromJson === "string" ? JSON.parse(s.FromJson) : s.FromJson)}
                  toAddress={toRouteAddress(typeof s.ToJson === "string" ? JSON.parse(s.ToJson) : s.ToJson)}
                  fromPartyName={s.RetailerName}
                  toPartyName={t("You")}
                />
              </div>
              <div style={{ marginTop: 8, fontSize: 12.5, color: "#6b7689" }}>
                {s.Carrier} · {s.TrackingNo} · {fmtMoney(s.DeclaredValue)}
              </div>
            </div>
          ))
        )}
      </Spin>
    </div>
  );
};

export default ReturnsInbound;
